import React, { Dispatch } from "react";
import {
  DropdownItem,
  DropdownToggle,
  DropdownMenu,
  Button,
  ButtonDropdown,
  Alert
} from "reactstrap";
import { connect } from "react-redux";
import { Icon } from "@cadmakers-ui/icons";
import { showSnack } from "react-redux-snackbar";
import { withRouter, RouteComponentProps } from "react-router";
import { Feedback } from "../../Models/Feedback";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../../../Layout";
import { IAppState, ApiRequest } from "../../../Common";
import { Application } from "../../../App";
import { Topic } from "../../../Topic/Models/Topic";
import {
  FeedbackParent,
  FeedbackCategory,
  FeedbackState,
  FeedbackEditType
} from "../../Constants/feedbackEnums";
import "../../Styles/MoveFeedbackModal.scss";
import ElementSpacer from "../../../Layout/Components/ElementSpacer";
import { endpoints } from "../../Constants/endpoints";
import ResponseHandler from "../../../App/ResponseHandler";
import { feedbackService } from "../../Services";
import EditFeedbackModal from "./EditFeedbackModal";
import { StatusCode } from "../../../App/Enums/StatusCode";
import { feedbackCategoryService } from "../../Services/FeedbackCategoryService";
import iconStyles from "../../../Common/Styles/Icons.module.scss";

type Props = {
  isModalOpen: boolean;
  feedback: Feedback;
  toggleModalCallback: () => void;
  onSave?: (feedback: Feedback) => void;
  onOverriddenSave?: (
    parent: FeedbackParent,
    category: FeedbackCategory,
    app?: Application,
    topic?: Topic
  ) => void;
  preventDefaultHanding: boolean;
  onReloadList?: () => void;
};

type State = {
  isParentOpen: boolean;
  isAppOpen: boolean;
  isTopicOpen: boolean;
  isCategoryOpen: boolean;
  selectedApp?: Application;
  selectedTopic?: Topic;
  selectedCategory: FeedbackCategory;
  selectedParent: FeedbackParent;
  isEditOpen: boolean;
  lock: boolean;
};

type StateProps = {
  applications: Application[];
  topics: Topic[];
  selectedApp?: Application;
};

type DispatchProps = {
  showSnack: (uniqueId: any, args: any) => void;
};

const mapStateToProps = (state: IAppState): StateProps => ({
  applications: state.app.applications,
  topics: state.app.topics,
  selectedApp: state.app.activeApp
});

/* eslint-disable no-shadow */
enum DropdownType {
  Parent = "Parent",
  Application = "Application",
  Topic = "Topic",
  Category = "Category"
}
/* eslint-enable no-shadow */

const CLASSNAME_PREFIX = "MoveFeedbackModal__";

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchProps => ({
  showSnack: (uniqueId: any, args: any) => dispatch(showSnack(uniqueId, args))
});

class MoveFeedbackModal extends React.Component<
  Props & StateProps & DispatchProps & RouteComponentProps,
  State
> {
  static defaultProps = {
    preventDefaultHanding: false
  };

  constructor(props: Props & StateProps & DispatchProps & RouteComponentProps) {
    super(props);

    this.state = {
      isParentOpen: false,
      isAppOpen: false,
      isTopicOpen: false,
      isCategoryOpen: false,
      selectedApp: props.selectedApp,
      selectedTopic: props.feedback.topic,
      selectedCategory: FeedbackCategory[props.feedback.category as keyof typeof FeedbackCategory],
      selectedParent: props.feedback.parent,
      isEditOpen: false,
      lock: false
    };
  }

  resetSelections = () => {
    this.setState({
      selectedApp: this.props.feedback.application,
      selectedTopic: this.props.feedback.topic,
      selectedCategory:
        FeedbackCategory[this.props.feedback.category as keyof typeof FeedbackCategory],
      selectedParent: this.props.feedback.parent
    });
  };

  lock = () => this.setState({ lock: true });

  unlock = () => this.setState({ lock: false });

  toggleEditModal = () => this.setState(prevState => ({ isEditOpen: !prevState.isEditOpen }));

  getAppOptions = () => {
    return this.props.applications.map(app => (
      <DropdownItem
        key={app.rolePrefix}
        active={
          this.state.selectedApp ? this.state.selectedApp.rolePrefix === app.rolePrefix : false
        }
        onClick={() => this.setState({ selectedApp: app })}
      >
        {app.name}
      </DropdownItem>
    ));
  };

  getTopicOptions = () => {
    return this.props.topics.map(topic => (
      <DropdownItem
        key={topic.code}
        active={this.state.selectedTopic ? this.state.selectedTopic.code === topic.code : false}
        onClick={() => this.setState({ selectedTopic: topic })}
      >
        {topic.name}
      </DropdownItem>
    ));
  };

  getParentOptions = () => {
    const options = [
      <DropdownItem
        key="parent_app"
        active={this.state.selectedParent === FeedbackParent.Application}
        onClick={() =>
          this.setState({ selectedParent: FeedbackParent.Application, selectedTopic: undefined })
        }
      >
        {FeedbackParent.Application}
      </DropdownItem>
    ];

    if (this.props.topics.length > 0) {
      options.push(
        <DropdownItem
          key="parent_topic"
          active={this.state.selectedParent === FeedbackParent.Topic}
          onClick={() =>
            this.setState({
              selectedParent: FeedbackParent.Topic,
              selectedApp: undefined,
              selectedCategory: FeedbackCategory.FeatureRequest
            })
          }
        >
          {FeedbackParent.Topic}
        </DropdownItem>
      );
    }

    return options;
  };

  getCategoryOptions = () => {
    const options = [
      <DropdownItem
        key="category_feature_request"
        active={this.state.selectedCategory === FeedbackCategory.FeatureRequest}
        onClick={() => this.setState({ selectedCategory: FeedbackCategory.FeatureRequest })}
      >
        Feature Request
      </DropdownItem>
    ];

    if (this.state.selectedParent === FeedbackParent.Application) {
      options.push(
        <DropdownItem
          key="category_bug_report"
          active={this.state.selectedCategory === FeedbackCategory.BugReport}
          onClick={() => this.setState({ selectedCategory: FeedbackCategory.BugReport })}
        >
          Bug Report
        </DropdownItem>
      );

      if (this.state.selectedApp?.allowResourceRequest) {
        options.push(
          <DropdownItem
            key="category_resource_request"
            active={this.state.selectedCategory === FeedbackCategory.ResourceRequest}
            onClick={() => this.setState({ selectedCategory: FeedbackCategory.ResourceRequest })}
          >
            Resource Request
          </DropdownItem>
        );
      }
    }

    return options;
  };

  getParentDropdown = () => {
    return (
      <ButtonDropdown
        isOpen={this.state.isParentOpen}
        toggle={() => this.onDropdownToggle(DropdownType.Parent)}
      >
        <DropdownToggle caret className={`${CLASSNAME_PREFIX}dropdown-toggle`}>
          {this.state.selectedParent}
        </DropdownToggle>
        <DropdownMenu>{this.getParentOptions()}</DropdownMenu>
      </ButtonDropdown>
    );
  };

  getApplicationDropdown = () => {
    return (
      <ButtonDropdown
        isOpen={this.state.isAppOpen}
        toggle={() => this.onDropdownToggle(DropdownType.Application)}
      >
        <DropdownToggle caret className={`${CLASSNAME_PREFIX}dropdown-toggle`}>
          {this.state.selectedApp ? this.state.selectedApp.name : "Select an application"}
        </DropdownToggle>
        <DropdownMenu>{this.getAppOptions()}</DropdownMenu>
      </ButtonDropdown>
    );
  };

  getTopicDropdown = () => {
    return (
      <ButtonDropdown
        className="MoveFeedbackModal__topic-dropdown"
        isOpen={this.state.isTopicOpen}
        toggle={() => this.onDropdownToggle(DropdownType.Topic)}
      >
        <DropdownToggle caret className={`${CLASSNAME_PREFIX}dropdown-toggle`}>
          {this.state.selectedTopic ? this.state.selectedTopic.name : "Select a topic"}
        </DropdownToggle>
        <DropdownMenu>{this.getTopicOptions()}</DropdownMenu>
      </ButtonDropdown>
    );
  };

  getCategoryDropdown = () => {
    return (
      <ButtonDropdown
        isOpen={this.state.isCategoryOpen}
        toggle={() => this.onDropdownToggle(DropdownType.Category)}
      >
        <DropdownToggle caret className={`${CLASSNAME_PREFIX}dropdown-toggle`}>
          {feedbackCategoryService.getDisplayNameForCategory(this.state.selectedCategory)}
        </DropdownToggle>
        <DropdownMenu>{this.getCategoryOptions()}</DropdownMenu>
      </ButtonDropdown>
    );
  };

  onToggle = () => {
    this.props.toggleModalCallback();
    this.resetSelections();
  };

  onDropdownToggle = (type: DropdownType) => {
    switch (type) {
      case DropdownType.Parent:
        this.setState(prevState => ({ isParentOpen: !prevState.isParentOpen }));
        break;
      case DropdownType.Application:
        this.setState(prevState => ({ isAppOpen: !prevState.isAppOpen }));
        break;
      case DropdownType.Topic:
        this.setState(prevState => ({ isTopicOpen: !prevState.isTopicOpen }));
        break;
      case DropdownType.Category:
        this.setState(prevState => ({ isCategoryOpen: !prevState.isCategoryOpen }));
        break;
      default:
        throw new Error(`Invalid dropdown type "${type}"`);
    }
  };

  appendSelections = (feedback: Feedback) => {
    return {
      ...feedback,
      parent: this.state.selectedParent,
      application: this.state.selectedApp as Application,
      topic: this.state.selectedTopic as Topic,
      category: this.state.selectedCategory
    };
  };

  onSave = () => {
    const feedback = this.appendSelections(this.props.feedback);

    if (!this.validateSelections()) {
      return;
    }

    if (!this.hasChanged()) {
      this.onToggle();
      return;
    }

    if (this.props.feedback.category !== this.state.selectedCategory) {
      feedback.state = FeedbackState.New;
      this.setState({
        isEditOpen: true
      });
    } else {
      this.updateFeedback(feedback, () => {
        if (this.props.onSave) {
          this.props.onSave(feedback);
        }
        this.onToggle();
      });
    }
  };

  onOverriddenSave = () => {
    if (!this.validateSelections()) {
      return;
    }

    if (this.props.onOverriddenSave) {
      this.props.onOverriddenSave(
        this.state.selectedParent,
        this.state.selectedCategory,
        this.state.selectedApp,
        this.state.selectedTopic
      );
      this.onToggle();
    }
  };

  onEditSave = (editedFeedback: Feedback) => {
    const feedback = this.appendSelections(editedFeedback);

    if (!this.validateSelections()) {
      return;
    }

    if (!this.hasChanged()) {
      this.onToggle();
      return;
    }

    this.updateFeedback(feedback, () => {
      if (this.props.onSave) {
        this.props.onSave(feedback);
      } else if (this.props.onReloadList) {
        this.props.onReloadList();
      }
      this.toggleEditModal();
      this.onToggle();
    });
  };

  hasChanged = () => {
    if (
      this.props.feedback.parent === this.state.selectedParent &&
      this.props.feedback.category === this.state.selectedCategory &&
      ((!!this.props.feedback.application &&
        !!this.state.selectedApp &&
        this.props.feedback.application.rolePrefix === this.state.selectedApp.rolePrefix) ||
        (!!this.props.feedback.topic &&
          !!this.state.selectedTopic &&
          this.props.feedback.topic.code === this.state.selectedTopic.code))
    ) {
      return false;
    }

    return true;
  };

  validateSelections = () => {
    switch (this.state.selectedParent) {
      case FeedbackParent.Application:
        if (!this.state.selectedApp) {
          this.showErrorSnackbar("You must select an application");
          return false;
        }
        if (
          this.state.selectedCategory === FeedbackCategory.ResourceRequest &&
          !this.state.selectedApp?.allowResourceRequest
        ) {
          this.showErrorSnackbar("This app cannot have resource requests");
        }
        break;
      case FeedbackParent.Topic:
        if (!this.state.selectedTopic) {
          this.showErrorSnackbar("You must select a topic");
          return false;
        }

        if (this.state.selectedCategory === FeedbackCategory.BugReport) {
          this.showErrorSnackbar("Topics cannot have bug reports");
          return false;
        }

        if (this.state.selectedCategory === FeedbackCategory.ResourceRequest) {
          this.showErrorSnackbar("Topics cannot have resource requests");
          return false;
        }
        break;
      default:
        if (!Object.values(FeedbackCategory).includes(this.state.selectedCategory)) {
          throw new Error(`Invalid feedback parent ${this.state.selectedParent}`);
        }
    }

    return true;
  };

  updateFeedback = (feedback: Feedback, callback: () => void) => {
    if (this.state.lock) {
      return;
    }

    this.lock();

    ApiRequest.fromPath(endpoints.feedback.update(this.props.feedback.code))
      .withAuthHeader()
      .withJson(feedbackService.createSubmitModel(feedback, FeedbackEditType.Move))
      .put()
      .then(response => {
        ResponseHandler.handle(
          response,
          () => {
            callback();
          },
          result => this.showResponseError(response.status, result.error.message),
          () => this.showResponseError(response.status, "An unknown error has occured")
        );
      })
      .catch(() =>
        this.showResponseError(StatusCode.Status503ServiceUnavailable, "Unable to contact server")
      )
      .finally(() => this.unlock());
  };

  showResponseError = (errorCode: number, errorDescription: string) => {
    this.showErrorSnackbar(
      `Failed to move feedback: ${errorDescription} (Error Code ${errorCode})`
    );
  };

  showErrorSnackbar = (message: string) => {
    this.props.showSnack("MOVE_FEEDBACK_MODAL", {
      label: message,
      timeout: 4000
    });
  };

  render() {
    return (
      <div>
        <Modal
          width="700px"
          height="500px"
          isOpen={this.props.isModalOpen}
          onToggle={this.onToggle}
        >
          <ModalHeader toggle={this.onToggle}>Move Feedback</ModalHeader>
          <ModalBody>
            <div className={`${CLASSNAME_PREFIX}body-container`}>
              {!this.props.preventDefaultHanding &&
              this.props.feedback.category !== this.state.selectedCategory ? (
                <div className={`${CLASSNAME_PREFIX}note`}>
                  <Alert color="danger">
                    <small>
                      <span style={{ fontWeight: "bold" }}>Warning:</span> Moving a feedback to a
                      different category will reset its state to the start state of that
                      category&apos;s workflow and you will be required to enter new values for that
                      category&apos;s fields.
                    </small>
                  </Alert>
                </div>
              ) : null}
              <div className={`${CLASSNAME_PREFIX}state-change-container`}>
                <div className={`${CLASSNAME_PREFIX}previous-state-container`}>
                  <div className={`${CLASSNAME_PREFIX}previous-state-item`}>
                    {this.props.feedback.parent}
                  </div>
                  <div className={`${CLASSNAME_PREFIX}previous-state-item`}>
                    {this.props.feedback.parent === FeedbackParent.Application
                      ? this.props.feedback.application.name
                      : this.props.feedback.topic.name}
                  </div>
                  <div className={`${CLASSNAME_PREFIX}previous-state-item`}>
                    {this.props.feedback.category}
                  </div>
                </div>
                <div className={`${CLASSNAME_PREFIX}transition-icon-container`}>
                  <span className={`${CLASSNAME_PREFIX}transition-icon`}>
                    <Icon
                      appName="GeneralLayout"
                      assetName="GENERAL_F_Arrow"
                      className={iconStyles.smallIcon}
                    />
                  </span>
                </div>

                <div className={`${CLASSNAME_PREFIX}new-state-container`}>
                  <div className={`${CLASSNAME_PREFIX}dropdown`}>{this.getParentDropdown()}</div>
                  <div className={`${CLASSNAME_PREFIX}dropdown`}>
                    {this.state.selectedParent === FeedbackParent.Application
                      ? this.getApplicationDropdown()
                      : this.getTopicDropdown()}
                  </div>
                  <div className={`${CLASSNAME_PREFIX}dropdown`}>{this.getCategoryDropdown()}</div>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <ElementSpacer>
              <Button
                onClick={
                  this.props.preventDefaultHanding
                    ? () => this.onOverriddenSave()
                    : () => this.onSave()
                }
                disabled={this.state.lock}
              >
                Update
              </Button>
              <Button onClick={this.onToggle}>Cancel</Button>
            </ElementSpacer>
          </ModalFooter>
        </Modal>

        <EditFeedbackModal
          feedback={this.props.feedback}
          category={this.state.selectedCategory}
          isOpen={this.state.isEditOpen}
          onToggle={this.toggleEditModal}
          onSave={this.onEditSave}
        />
      </div>
    );
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(MoveFeedbackModal));
